/**
 * @file DissentWithoutPsychologyAssessmentListView.jsx
 * @author Daniel Aguirre
 * @description Dashboard Detail Management View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Helpers
import { useParams } from 'react-router';
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSorterString,
  getSelectFilter,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function DissentWithoutPsychologyAssessmentListView({
  dashboardStaffActions,
  clinicId
}) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getDissentWithoutPsychologyAssessmentListPlus } =
    dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.lastValuationDate'),
      dataIndex: 'fecha_ultima_valoracion',
      filter: {
        listName: 'filtro_fecha_ultima_valoracion',
        searchName: 'filtroFechaUltimaValoracion',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultima_valoracion'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'filtro_fecha_ultima_valoracion',
            searchName: 'filtroFechaUltimaValoracion'
          },
          dataIndex: 'fecha_ultima_valoracion'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.revisionType'),
      dataIndex: 'tipo_revision',
      filter: {
        listName: 'filtro_tipo_revision',
        searchName: 'filtroTipoRevision',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('tipo_revision'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'filtro_tipo_revision',
            searchName: 'filtroTipoRevision'
          },
          dataIndex: 'tipo_revision'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.optionsTreatmentsLastDate'),
      dataIndex: 'fecha_ultimo_registro_opciones_tratamientos',
      filter: {
        listName: 'filtro_fecha_ultimo_registro_opciones_tratamiento',
        searchName: 'filtroFechaUltimoRegistroOpcionesTratamiento',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultimo_registro_opciones_tratamientos'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'filtro_fecha_ultimo_registro_opciones_tratamiento',
            searchName: 'filtroFechaUltimoRegistroOpcionesTratamiento'
          },
          dataIndex: 'fecha_ultimo_registro_opciones_tratamientos'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        registroValoracionPsicologiaUltimos6MesesSeleccionado: classification,
        idClinic: clinicId,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getDissentWithoutPsychologyAssessmentListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            fecha_ultima_valoracion: item?.fecha_ultima_valoracion
              ? formatDate(item.fecha_ultima_valoracion)
              : '',
            fecha_ultimo_registro_opciones_tratamientos:
              item?.fecha_ultimo_registro_opciones_tratamientos
                ? formatDate(item.fecha_ultimo_registro_opciones_tratamientos)
                : ''
          };
        });

        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
DissentWithoutPsychologyAssessmentListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getDissentWithoutPsychologyAssessmentListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default DissentWithoutPsychologyAssessmentListView;
